import * as React from 'react'

import { BgColorProvider } from '~theme/BgColorProvider'
import { CompanyHero } from '~ui/components/CompanyHero/CompanyHero'
import { CompanyHeroTopLine } from '~ui/components/CompanyHero/CompanyHeroTopLine'
import { Box, Grid } from '~ui/components/layout/Box'
import { useTheme } from '~ui/theme/ThemeProvider'

type TProps = RequiredChildren & {
  /**
   * whether the screen is mobile
   */
  isMobile?: boolean
  /**
   * whether the screen is tablet or mobile
   */
  isTabletOrMobile: boolean
  /**
   * whether the screen is short (e. g. height < 900px)
   */
  isShortScreen: boolean
  /**
   * additional content that should be added to the top line of the CompanyHero (next to the LanguagePicker)
   */
  topLineContent?: PropsOf<typeof CompanyHeroTopLine>['children']
  /**
   * whether the screen should be locked to one full screen page (~100vh) or can be scrolled
   */
  fixedFullScreen?: boolean
  /**
   * how much should the hero image overflow into the content that is bellow it
   * is used inside a CSS calc so it can be something like `20px + 4vh` etc
   * this value is ignored on mobile!
   */
  imageOverlap?: string
  /**
   * whether the company description should be shown or not
   */
  withCompanyDescription?: boolean
  /**
   * the footer of the screen
   */
  footer: React.ReactNode
  /**
   * max width of the content
   */
  maxContentWidth?: PropsOf<typeof Box>['width']
  /**
   * header text of the brand or branch
   */
  headerText?: Nullable<string>
  /**
   * promo header text of the brand or branch
   */
  promoHeaderText: Nullable<string>
  /**
   * company name label
   */
  companyNameLabel: string
  /**
   * company logo image url
   */
  companyLogoUrl: string
  /**
   * hero image url
   */
  heroImageUrl: string
}

const firsRowProps: PropsOf<typeof Box> = {
  gridRow: 1,
  gridColumn: 1,
}

// TODO test 100vh on real mobile device (might not work with browser nav bars etc)

/**
 * Screen wrapper that renders its children inside a layout with company hero image & info on the top.
 * Also includes the LanguagePicker and SpeedloFooter (footer is omitted on mobile screens).
 */
export const HeroScreen: React.FC<TProps> = ({
  children,
  topLineContent,
  maxContentWidth,
  withCompanyDescription = false,
  fixedFullScreen = false,
  imageOverlap = `20px + 4vh`,
  footer,
  headerText,
  promoHeaderText,
  isMobile,
  isTabletOrMobile,
  isShortScreen,
  companyLogoUrl,
  companyNameLabel,
  heroImageUrl,
}) => {
  const theme = useTheme()

  // desktop header text of the brand or branch (chosen automatically)
  // if there is a promo header text, it has priority
  const isPromoHeaderText = Boolean(promoHeaderText?.trim())
  const headerDescription = isPromoHeaderText ? promoHeaderText : headerText

  const containerProps: PropsOf<typeof Box> = {
    position: `relative`, // because of position on the bg image
    margin: { vertical: `none`, horizontal: `auto` }, // center the content
    maxWidth: maxContentWidth,
    width: isMobile ? `100%` : `90%`,
  }

  // There is always padding on desktop and on tablet when it does not have promo header text.
  // Mobile screen doesn’t have padding - doesn’t matter if it has promo header text or not.
  const isTabletPromo = isTabletOrMobile && promoHeaderText

  const shouldHavePaddingBottom = !(isMobile || isTabletPromo)
  const boxBottomPadding = shouldHavePaddingBottom ? `extraLarge` : `none`

  return (
    <>
      <Grid
        zIndex={2} // TODO zIndex
        position={`relative`}
        isolation={`isolate`}
        minHeight={`100vh`}
        height={fixedFullScreen ? `100vh` : undefined}
        gridTemplateRows={`min-content minmax(0, 1fr) min-content`} // https://css-tricks.com/preventing-a-grid-blowout/
      >
        <BgColorProvider bgColor={`specialColors.overlayMedium`}>
          {/* main bg image & overlay */}
          <Box {...firsRowProps} position={`relative`}>
            <Box
              width={`100%`}
              position={`absolute`}
              backgroundSize={`cover`}
              backgroundPosition={`center`}
              backgroundRepeat={`no-repeat`}
              height={isMobile ? `100%` : `calc(100% + ${imageOverlap})`}
              backgroundImage={`linear-gradient(${theme.specialColors.overlayLite},${theme.specialColors.overlayMedium}), url("${heroImageUrl}")`}
            />
          </Box>

          {/* company info (logo, nameLabel, header text, top line) */}
          <Box
            {...firsRowProps}
            {...containerProps}
            padding={{
              top: `medium`,
              left: isMobile ? `medium` : `none`,
              right: isMobile ? `medium` : `none`,
              bottom: isShortScreen ? `largePlus` : `extraLarge`,
            }}
          >
            <CompanyHero
              topLine={topLineContent}
              company={{
                logoUrl: companyLogoUrl,
                companyNameLabel,
                description: withCompanyDescription ? headerDescription : undefined,
                isPromoHeaderText,
              }}
              // prettier-ignore
              layout={isTabletOrMobile
            ? `mobile`
            : isShortScreen
              ? `compact`
              : `desktop`
          }
            />
          </Box>
        </BgColorProvider>

        {/* the page content */}
        <Box
          {...containerProps}
          padding={{
            bottom: boxBottomPadding,
            top: `none`,
            left: `none`,
            right: `none`,
          }}
        >
          {children}
        </Box>
      </Grid>

      {/* footer */}
      <Box bgColor={`bgColors.card`} isolation={`isolate`} position={`relative`} zIndex={1}>
        <Box {...containerProps}>{footer}</Box>
      </Box>
    </>
  )
}
